import { FolderOpen, CalendarCheck, TrendingUp, BarChart3, BellRing, RefreshCw, Layers, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import SectionHeader from './SectionHeader'
import Tag from './Tag'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const features = [
  { icon: FolderOpen, title: 'Multi-Portfolio Management', desc: 'Organise holdings across retirement, taxable, and dividend growth accounts — all in one place.', tier: 'Premium' },
  { icon: CalendarCheck, title: 'Dividend Calendar', desc: 'Never miss a payment. See every ex-dividend and payment date on a clean visual calendar.', tier: 'Free' },
  { icon: TrendingUp, title: 'DRIP Snowball Projections', desc: 'See exactly how reinvesting dividends compounds your income over 5, 10, or 20 years.', tier: 'Free', link: '/drip-calculator' },
  { icon: BarChart3, title: 'Advanced Income Reports', desc: 'Annual breakdowns, yield-on-cost analysis, and CSV exports ready for tax season.', tier: 'Premium' },
  { icon: BellRing, title: 'Smart Alerts', desc: 'Get notified when yields change, cuts are announced, or a holding hits your custom threshold.', tier: 'Premium' },
  { icon: RefreshCw, title: 'Brokerage Sync', desc: 'Connect your broker via SnapTrade. Import holdings automatically — no manual entry.', tier: 'Premium' },
  { icon: Layers, title: 'Covered Call & CSP Tracker', desc: 'Track covered calls and cash-secured puts alongside dividends. Log premiums, monitor open positions, and see net P&L per trade.', tier: 'Pro', link: '/covered-call-tracker' },
  { icon: ShieldCheck, title: 'Bank-Grade Security', desc: 'HTTPS-only, bcrypt passwords, JWT auth, 2FA. Brokerage credentials never stored on our servers.', tier: 'All' },
]

const tierStyles = {
  'Premium': 'gold',
  'Free': 'green',
  'Pro': 'charcoal',
  'All': 'slate',
}

export default function FeaturesGrid() {
  const ref = useScrollAnimation({ childSelector: '.feature-card', stagger: 0.1 })

  return (
    <section className="bg-[#F8FAFC] section-padding">
      <div className="content-max">
        <SectionHeader
          overline="Built for serious income investors"
          headline="Every feature designed around one goal"
          subtext="Helping you build and grow reliable passive income."
        />

        <div ref={ref} className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon
            const card = (
              <div
                key={feature.title}
                className={`feature-card bg-white border border-[#E2E8F0] rounded-xl p-8 hover:shadow-card-hover hover:border-[#22C55E] hover:-translate-y-1 transition-all duration-300 ${feature.link ? 'cursor-pointer' : ''}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 bg-[#F0FDF4] rounded-lg">
                    <Icon className="w-7 h-7 text-[#1B5E20] stroke-[1.5]" />
                  </div>
                  <Tag variant={tierStyles[feature.tier]}>{feature.tier}</Tag>
                </div>
                <h4 className="text-lg font-medium text-[#0F172A] mb-2">{feature.title}</h4>
                <p className="text-sm text-[#475569] leading-relaxed">{feature.desc}</p>
                {feature.link && (
                  <p className="mt-3 text-xs font-medium text-[#1B5E20]">Try the calculator →</p>
                )}
              </div>
            )
            return feature.link
              ? <Link key={feature.title} to={feature.link}>{card}</Link>
              : <div key={feature.title}>{card}</div>
          })}
        </div>
      </div>
    </section>
  )
}
